import { useEffect, useState } from 'react';
import { fetchAdminSession, loginAdmin, logoutAdmin } from './api/authApi';
import { fetchPublicAssets, SITE_ASSET_KEYS, uploadAdminAsset } from './api/assetsApi';
import AdminPage from './pages/AdminPage';
import UserPage from './pages/UserPage';
import { ADMIN_PATH } from './config/adminPath';

function App() {
  const isAdminPage = window.location.pathname.startsWith(ADMIN_PATH);
  const [assets, setAssets] = useState({});
  const [assetsError, setAssetsError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAdminSession, setIsCheckingAdminSession] = useState(isAdminPage && process.env.NODE_ENV !== 'test');
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({});

  const refreshAssets = async () => {
    try {
      const assetMap = await fetchPublicAssets();
      setAssets(assetMap);
      setAssetsError('');
    } catch (error) {
      setAssets({});
      setAssetsError('백엔드에서 이미지를 불러오지 못했습니다.');
    }
  };

  useEffect(() => {
    if (process.env.NODE_ENV === 'test') {
      return undefined;
    }

    refreshAssets();

    return undefined;
  }, []);

  useEffect(() => {
    if (!isAdminPage || process.env.NODE_ENV === 'test') {
      setIsCheckingAdminSession(false);
      return undefined;
    }

    let isMounted = true;

    const syncAdminSession = async () => {
      setIsCheckingAdminSession(true);

      try {
        const adminSession = await fetchAdminSession();

        if (!isMounted) {
          return;
        }

        setIsAuthenticated(Boolean(adminSession?.authenticated));
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setIsAuthenticated(false);
      } finally {
        if (isMounted) {
          setIsCheckingAdminSession(false);
        }
      }
    };

    syncAdminSession();

    return () => {
      isMounted = false;
    };
  }, [isAdminPage]);

  useEffect(() => {
    if (isAdminPage) {
      return undefined;
    }

    const faviconUrl = assets.FAVICON?.resolvedUrl;

    if (!faviconUrl) {
      return undefined;
    }

    const faviconElement = document.querySelector('link[rel="icon"]');

    if (!faviconElement) {
      return undefined;
    }

    const originalHref = faviconElement.getAttribute('href');
    faviconElement.setAttribute('href', faviconUrl);

    return () => {
      if (originalHref) {
        faviconElement.setAttribute('href', originalHref);
      }
    };
  }, [assets, isAdminPage]);

  const handleAdminLogin = async (event) => {
    event.preventDefault();

    if (!loginId.trim() || !password.trim()) {
      setLoginError('아이디와 비밀번호를 입력해주세요.');
      return;
    }

    setIsLoggingIn(true);
    setLoginError('');

    try {
      await loginAdmin(loginId.trim(), password);
      setIsAuthenticated(true);
      setPassword('');
    } catch (error) {
      setLoginError('로그인 실패: 아이디 또는 비밀번호를 확인해주세요.');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logoutAdmin();
    } finally {
      setIsAuthenticated(false);
      setUploadStatus({});
    }
  };

  const handleUpload = async (assetKey, file) => {
    if (!file) {
      return;
    }

    setUploadStatus((previous) => ({ ...previous, [assetKey]: '업로드 중...' }));

    try {
      await uploadAdminAsset(assetKey, file);
      await refreshAssets();
      setUploadStatus((previous) => ({ ...previous, [assetKey]: '✅ 완료' }));
      // 3초 후 메시지 제거
      setTimeout(() => setUploadStatus((previous) => ({ ...previous, [assetKey]: '' })), 3000);
    } catch (error) {
      if (error.status === 401) {
        setIsAuthenticated(false);
        setLoginError('세션이 만료되었습니다. 다시 로그인해주세요.');
      }
      setUploadStatus((previous) => ({ ...previous, [assetKey]: '❌ 실패' }));
    }
  };

  if (isAdminPage) {
    return (
      <AdminPage
        isAuthenticated={isAuthenticated}
        isCheckingAdminSession={isCheckingAdminSession}
        loginId={loginId}
        password={password}
        loginError={loginError}
        isLoggingIn={isLoggingIn}
        uploadStatus={uploadStatus}
        assets={assets}
        assetsError={assetsError}
        siteAssetKeys={SITE_ASSET_KEYS}
        onLoginIdChange={setLoginId}
        onPasswordChange={setPassword}
        onLogin={handleAdminLogin}
        onLogout={handleLogout}
        onRefreshAssets={refreshAssets}
        onUpload={handleUpload}
      />
    );
  }

  return <UserPage assets={assets} />;
}

export default App;
